const DOCUMENT_PUNCTUATION_REGEX = /[.\-/\s]/g;

export const CPF_MASK = '000.000.000-00';
export const CNPJ_MASK = 'AA.AAA.AAA/AAAA-DD';
export const DOCUMENT_MASK = 'CPF_CNPJ';

/** Remove pontuação e força uppercase. Preserva letras — usar em CNPJ, não em CPF. */
export function unmaskDocument(
  value: string | number | undefined | null,
): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  return String(value).replace(DOCUMENT_PUNCTUATION_REGEX, '').toUpperCase();
}

/** Apenas dígitos — manter para CPF. */
export function unmaskCpf(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  return String(value).replace(/[^\d]/g, '');
}

export function cleanCnpjInput(value: string): string {
  const base = value
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase()
    .slice(0, 14);

  const root = base.slice(0, 12).replace(/[^A-Z0-9]/g, '');
  const dv = base.slice(12).replace(/\D/g, '').slice(0, 2);

  return `${root}${dv}`.slice(0, 14);
}

export function maskCpf(value: string | number | undefined | null): string {
  const cleaned = unmaskCpf(value).slice(0, 11);
  if (!cleaned) {
    return '';
  }

  let masked = cleaned;
  masked = masked.replace(/^(\d{3})(\d)/, '$1.$2');
  masked = masked.replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3');
  masked = masked.replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
  return masked;
}

export function maskCnpj(value: string | number | undefined | null): string {
  const cleaned = cleanCnpjInput(String(value ?? ''));
  if (!cleaned) {
    return '';
  }

  let maskedValue = cleaned;
  if (maskedValue.length > 2) {
    maskedValue = maskedValue.replace(/^(.{2})(.+)/, '$1.$2');
  }
  if (maskedValue.length > 6) {
    maskedValue = maskedValue.replace(/^(.{2}\.)(.{3})(.+)/, '$1$2.$3');
  }
  if (maskedValue.length > 10) {
    maskedValue = maskedValue.replace(/^(.{2}\.{1}.{3}\.)(.{3})(.+)/, '$1$2/$3');
  }
  if (maskedValue.length > 15) {
    maskedValue = maskedValue.replace(
      /^(.{2}\.{1}.{3}\.{1}.{3}\/)(.{4})(.+)/,
      '$1$2-$3',
    );
  }
  maskedValue = maskedValue.replace(/(-\d{2}).+$/, '$1');

  return maskedValue;
}

export function maskDocument(value: string | number | undefined | null): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const alphanumeric = String(value)
    .replace(/[^a-zA-Z0-9]/g, '')
    .toUpperCase();
  const hasLetters = /[A-Z]/.test(alphanumeric);
  const digitsOnly = unmaskCpf(value);

  if (!hasLetters && digitsOnly.length <= 11) {
    return maskCpf(value);
  }

  return maskCnpj(value);
}

export function isDocumentComplete(
  value: string | number | undefined | null,
): boolean {
  if (value === undefined || value === null || value === '') {
    return false;
  }

  const unmasked = unmaskDocument(value);
  const hasLetters = /[A-Z]/.test(unmasked.slice(0, 12));

  if (hasLetters) {
    return unmasked.length === 14;
  }

  const digitsOnly = unmaskCpf(value);
  return digitsOnly.length === 11 || digitsOnly.length === 14;
}

export function isCnpjDocument(
  value: string | number | undefined | null,
): boolean {
  return unmaskDocument(value).length === 14;
}

export function isCpfDocument(value: string | number | undefined | null): boolean {
  const unmasked = unmaskDocument(value);
  return unmasked.length === 11 && !/[A-Z]/.test(unmasked);
}

export function normalizeDocumentForApi(
  value: string | number | undefined | null,
): string {
  const unmasked = unmaskDocument(value);
  const hasLetters = /[A-Z]/.test(unmasked.slice(0, 12));

  if (hasLetters || unmasked.length === 14) {
    return unmasked;
  }

  return unmaskCpf(value);
}

export function formatDocumentForDisplay(
  value: string | number | undefined | null,
): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  return maskDocument(value);
}

export function getDocumentTypeLabel(
  value: string | number | undefined | null,
): string {
  return isCnpjDocument(value) ? 'CNPJ' : 'CPF';
}

const getCnpjCharValue = (char: string): number => {
  return char.toUpperCase().charCodeAt(0) - 48;
};

const validateCnpjCheckDigit = (
  values: number[],
  position: number,
): boolean => {
  let weights: number[];
  let arrayItems: number;

  if (position === 1) {
    weights = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    arrayItems = 12;
  } else {
    weights = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    arrayItems = 13;
  }

  let sum = 0;
  for (let i = 0; i < arrayItems; i += 1) {
    sum += weights[i] * values[i];
  }

  const division = Math.floor(sum % 11);
  let verifyingDigit = 0;
  if (division >= 2) {
    verifyingDigit = 11 - division;
  }

  return values[arrayItems] === verifyingDigit;
};

const isRepeatedCnpjValues = (values: number[]): boolean => {
  return values.every((value) => value === values[0]);
};

const validateCnpjNumeric = (cnpj: string): boolean => {
  if (!/^\d{14}$/.test(cnpj)) {
    return false;
  }

  if (
    cnpj === '00000000000000' ||
    cnpj === '11111111111111' ||
    cnpj === '22222222222222' ||
    cnpj === '33333333333333' ||
    cnpj === '44444444444444' ||
    cnpj === '55555555555555' ||
    cnpj === '66666666666666' ||
    cnpj === '77777777777777' ||
    cnpj === '88888888888888' ||
    cnpj === '99999999999999'
  ) {
    return false;
  }

  const values = Array.from(cnpj, (char) => parseInt(char, 10));
  if (isRepeatedCnpjValues(values)) {
    return false;
  }

  return (
    validateCnpjCheckDigit(values, 1) && validateCnpjCheckDigit(values, 2)
  );
};

const validateCnpjAlphanumeric = (cnpj: string): boolean => {
  if (!/^[A-Z0-9]{12}\d{2}$/.test(cnpj)) {
    return false;
  }

  const values = Array.from(cnpj, getCnpjCharValue);
  if (isRepeatedCnpjValues(values)) {
    return false;
  }

  return (
    validateCnpjCheckDigit(values, 1) && validateCnpjCheckDigit(values, 2)
  );
};

export function validateCnpj(cnpj: unknown): boolean {
  if (typeof cnpj !== 'string' && typeof cnpj !== 'number') {
    return false;
  }

  const filteredCnpj = unmaskDocument(String(cnpj));
  if (filteredCnpj.length !== 14) {
    return false;
  }

  if (/[A-Z]/.test(filteredCnpj.slice(0, 12))) {
    return validateCnpjAlphanumeric(filteredCnpj);
  }

  return validateCnpjNumeric(filteredCnpj);
}

export function validateDocument(value: unknown): boolean {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return false;
  }

  const unmasked = unmaskDocument(String(value));
  if (unmasked.length === 11 && !/[A-Z]/.test(unmasked)) {
    // CPF validation delegated to BrValidators.cpf in utils
    return false;
  }

  if (unmasked.length === 14) {
    return validateCnpj(unmasked);
  }

  return false;
}

export type DocumentMaskType = 'cpf' | 'cnpj' | 'cpfOrCnpj';

export function applyDocumentMask(
  value: string,
  documentType: DocumentMaskType,
): string {
  switch (documentType) {
    case 'cpf':
      return maskCpf(value);
    case 'cnpj':
      return maskCnpj(value);
    case 'cpfOrCnpj':
    default:
      return maskDocument(value);
  }
}
